import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import ImagemProduto from '@/components/feature/ImagemProduto';
import Folha from '@/components/movimento/Folha';
import type { ItemCarteira, PontoCarteira, ValeCriado } from '@/pages/loja/types';

interface Props {
  carteiraCodigo: string;
  ponto: PontoCarteira;
  itens: ItemCarteira[];
  /** Produto tocado: já abre no máximo; os outros começam em zero. */
  itemFoco?: number | null;
  aoFechar: () => void;
  aoGerado: () => void;
}

// A retirada abre na folha padrão do app (sobe de baixo, arrasta para fechar),
// com o mesmo conteúdo de antes: quantidade por produto e o botão de gerar.
export default function ModalRetirada({
  carteiraCodigo,
  ponto,
  itens,
  itemFoco = null,
  aoFechar,
  aoGerado,
}: Props) {
  const [selecao, setSelecao] = useState<Record<number, number>>(() => {
    const mapa: Record<number, number> = {};
    itens.forEach((i) => {
      mapa[i.item_id] = itemFoco == null || i.item_id === itemFoco ? i.disponivel : 0;
    });
    return mapa;
  });
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  function definir(itemId: number, v: number) {
    const item = itens.find((i) => i.item_id === itemId);
    const maximo = item?.disponivel ?? 0;
    const valor = Math.min(Math.max(0, v), maximo);
    setSelecao((atual) => ({ ...atual, [itemId]: valor }));
  }

  const itensSelecionados = itens.filter((i) => (selecao[i.item_id] ?? 0) > 0);
  const totalFichas = itensSelecionados.reduce((soma, i) => soma + (selecao[i.item_id] ?? 0), 0);

  async function gerarVale() {
    setGerando(true);
    setErro(null);
    try {
      const { data, error } = await supabase.rpc('loja_criar_vale', {
        p_carteira: carteiraCodigo,
        p_ponto: ponto.id,
        p_itens: itensSelecionados.map((i) => ({
          item_id: i.item_id,
          quantidade: selecao[i.item_id],
        })),
      });
      if (error) throw error;
      const vale = (Array.isArray(data) ? data[0] : data) as ValeCriado | null;
      if (vale?.ok === false) {
        setErro('Não conseguimos gerar o vale. Tente de novo.');
        return;
      }
      aoGerado();
      aoFechar();
    } catch (e) {
      setErro(mensagemAmigavel(e, 'Não conseguimos gerar o vale. Tente de novo.'));
    } finally {
      setGerando(false);
    }
  }

  return (
    <Folha
      aberto
      aoFechar={aoFechar}
      titulo={`Retirar no ${ponto.nome}`}
      zIndex={50}
      larguraMaxima="max-w-[480px]"
      alturaMaxima="86vh"
    >
      <p className="-mt-1 text-[13px] text-foreground-600">
        Escolha só o que vai pegar agora.
      </p>

      <div className="mt-4 space-y-3">
        {itens.map((item) => {
          const qtd = selecao[item.item_id] ?? 0;
          return (
            <div
              key={item.item_id}
              className="rounded-[16px] border border-background-200 bg-background-50 p-3"
            >
              <div className="flex items-center gap-3">
                <ImagemProduto
                  imagemUrl={item.imagem_url}
                  imagemSlug={item.imagem_slug}
                  emoji={item.emoji}
                  nome={item.produto}
                  tamanho="media"
                />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[15px] font-bold text-foreground-950">
                    {item.produto}
                  </p>
                  <p className="text-[12px] text-foreground-600">
                    {item.disponivel} disponíve{item.disponivel === 1 ? 'l' : 'is'}
                  </p>
                </div>
              </div>

              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => definir(item.item_id, qtd - 1)}
                  disabled={qtd <= 0}
                  aria-label={`Tirar uma ${item.produto}`}
                  className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-[14px] border border-background-300 bg-background-50 text-[22px] font-bold text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  −
                </button>
                <span className="text-[26px] font-extrabold tabular-nums text-foreground-950">
                  {qtd}
                </span>
                <button
                  type="button"
                  onClick={() => definir(item.item_id, qtd + 1)}
                  disabled={qtd >= item.disponivel}
                  aria-label={`Pegar mais uma ${item.produto}`}
                  className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-[14px] border border-background-300 bg-background-50 text-[22px] font-bold text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {erro && (
        <p className="mt-4 rounded-[14px] border border-danger-200 bg-danger-50 px-4 py-3 text-[14px] text-danger-700">
          {erro}
        </p>
      )}

      <button
        type="button"
        onClick={gerarVale}
        disabled={gerando || itensSelecionados.length === 0}
        className="aperta mt-5 flex h-[56px] w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[16px] bg-success-500 text-[16px] font-extrabold text-white transition-colors hover:bg-success-600 disabled:cursor-not-allowed disabled:bg-background-300 disabled:text-foreground-500"
      >
        {gerando ? (
          <span className="flex items-center gap-2">
            <i className="ri-loader-4-line animate-spin" aria-hidden />
            Gerando…
          </span>
        ) : (
          <>
            <i className="ri-qr-scan-2-line" aria-hidden />
            Gerar vale
            {totalFichas > 0
              ? ` · ${totalFichas} ${totalFichas === 1 ? 'ficha' : 'fichas'}`
              : ''}
          </>
        )}
      </button>
      <p className="mt-2 text-center text-[12px] text-foreground-500">
        O resto continua guardado na sua carteira.
      </p>
    </Folha>
  );
}